import { useState, useEffect } from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import IOSInstallPrompt from "./IOSInstallPrompt";

const NAV = [
  { to: "/",           label: "DASHBOARD",  code: "01", exact: true },
  { to: "/summarize",  label: "SUMMARIZE",  code: "02" },
  { to: "/channels",   label: "CHANNELS",   code: "03" },
  { to: "/history",    label: "HISTORY",    code: "04" },
  { to: "/graph",      label: "GRAPH",      code: "05" },
  { to: "/automation", label: "AUTOMATION", code: "06" },
  { to: "/settings",   label: "SETTINGS",   code: "07" },
];

const MOBILE_BAR_H = 54;

function Logo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <img src="https://static.prod-images.emergentagent.com/jobs/9e668205-d010-4895-90ea-66d59c7423c3/images/cb47b83b9f3104563ca8f993c7e18f3f8d0937a5d53142d582f1d1392fb0d584.png" alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </div>
      <div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.18em", color: "#111", lineHeight: 1.2 }}>GIIST</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.18em", color: "#CC0000", lineHeight: 1.2 }}>.NET</div>
      </div>
    </div>
  );
}

function NavLinks({ onLinkClick, collapsed }) {
  const location = useLocation();
  return (
    <nav style={{ flex: 1, padding: "8px 0" }}>
      {NAV.map(({ to, label, code, exact }) => {
        const isActive = exact ? location.pathname === "/" : location.pathname.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase()}`}
            onClick={onLinkClick}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: collapsed ? "12px 0" : "11px 18px",
              justifyContent: collapsed ? "center" : "flex-start",
              borderLeft: isActive ? "3px solid #CC0000" : "3px solid transparent",
              background: isActive ? "rgba(204,0,0,0.04)" : "transparent",
              textDecoration: "none",
              transition: "all 0.1s",
            }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F5F5F3"; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
          >
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", fontWeight: 700, color: isActive ? "#CC0000" : "#bbb", letterSpacing: "0.05em", flexShrink: 0 }}>
              {code}
            </span>
            {!collapsed && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.14em", color: isActive ? "#111" : "#777" }}>
                {label}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: "#F0F0EC" }}>

      {/* ── MOBILE TOP BAR ──────────────────────────── */}
      {isMobile && (
        <header
          data-testid="mobile-topbar"
          style={{
            position: "fixed", top: 0, left: 0, right: 0,
            height: MOBILE_BAR_H,
            background: "#fff",
            borderBottom: "2px solid #111",
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
          }}
        >
          <Logo />
          <button
            data-testid="mobile-hamburger"
            onClick={() => setDrawerOpen(o => !o)}
            aria-label="Toggle menu"
            style={{
              background: drawerOpen ? "#111" : "transparent",
              border: "2px solid #111",
              cursor: "pointer",
              width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "1rem",
              color: drawerOpen ? "#fff" : "#111",
              lineHeight: 1,
              flexShrink: 0,
              transition: "background 0.12s, color 0.12s",
            }}
          >
            {drawerOpen ? "✕" : "≡"}
          </button>
        </header>
      )}

      {/* ── MOBILE DRAWER SIDEBAR ──────────────────── */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div
            data-testid="sidebar-backdrop"
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed", top: MOBILE_BAR_H, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 200,
              opacity: drawerOpen ? 1 : 0,
              pointerEvents: drawerOpen ? "all" : "none",
              transition: "opacity 0.25s ease",
            }}
          />
          {/* Drawer */}
          <aside
            data-testid="sidebar"
            style={{
              position: "fixed",
              top: MOBILE_BAR_H, left: 0, bottom: 0,
              width: 240,
              background: "#fff",
              borderRight: "2px solid #111",
              zIndex: 250,
              display: "flex",
              flexDirection: "column",
              transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.25s ease",
              overflowY: "auto",
            }}
          >
            <div style={{ padding: "20px 16px", borderBottom: "1px solid #e8e8e4" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.1em", color: "#888", marginBottom: 6 }}>OPERATOR</div>
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: "0.8rem", fontWeight: 600, color: "#111" }}>{user?.full_name || user?.email}</div>
            </div>
            <NavLinks onLinkClick={() => setDrawerOpen(false)} collapsed={false} />
            <div style={{ padding: 16, borderTop: "1px solid #e8e8e4", marginTop: "auto" }}>
              <button 
                onClick={handleLogout}
                style={{ width: "100%", background: "#111", color: "#fff", border: "none", padding: "10px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem", cursor: "pointer" }}
              >
                TERMINATE SESSION
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ── DESKTOP / TABLET SIDEBAR ──────────────── */}
      {!isMobile && (
        <aside
          data-testid="sidebar"
          style={{
            width: collapsed ? 56 : 200,
            flexShrink: 0,
            background: "#fff",
            borderRight: "2px solid #111",
            transition: "width 0.2s ease",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Logo */}
          <div style={{ borderBottom: "2px solid #111", padding: collapsed ? "16px 0" : "14px 16px", display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start" }}>
            {collapsed ? (
              <div style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img src="https://static.prod-images.emergentagent.com/jobs/9e668205-d010-4895-90ea-66d59c7423c3/images/cb47b83b9f3104563ca8f993c7e18f3f8d0937a5d53142d582f1d1392fb0d584.png" alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              </div>
            ) : <Logo />}
          </div>

          <NavLinks collapsed={collapsed} />

          {/* User & Logout */}
          <div style={{ marginTop: "auto", borderTop: "1px solid #e8e8e4" }}>
            {!collapsed && (
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e4" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#888", letterSpacing: "0.1em", marginBottom: 4 }}>OPERATOR</div>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: "0.75rem", fontWeight: 600, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {user?.full_name || user?.email?.split('@')[0]}
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              style={{
                width: "100%", background: "transparent", border: "none", cursor: "pointer",
                padding: "12px", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10,
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#F5F5F3"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              title="Logout"
            >
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.8rem", color: "#CC0000", fontWeight: 700 }}>⏻</div>
              {!collapsed && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#CC0000", fontWeight: 600, letterSpacing: "0.1em" }}>LOGOUT</span>}
            </button>
          </div>

          {/* Collapse toggle */}
          <div style={{ borderTop: "1px solid #e8e8e4", padding: "8px" }}>
            <button
              data-testid="sidebar-toggle"
              onClick={() => setCollapsed(c => !c)}
              style={{
                width: "100%", background: "transparent", border: "1px solid #ccc", cursor: "pointer",
                padding: "6px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#999", letterSpacing: "0.12em",
                transition: "border-color 0.1s, color 0.1s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#111"; e.currentTarget.style.color = "#111"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#ccc"; e.currentTarget.style.color = "#999"; }}
            >
              {collapsed ? "›" : "‹ HIDE"}
            </button>
          </div>
        </aside>
      )}

      {/* ── MAIN CONTENT ─────────────────────────────── */}
      <main
        className="dot-grid-bg"
        style={{
          flex: 1,
          overflowY: "auto",
          paddingTop: isMobile ? MOBILE_BAR_H + 20 : 32,
          paddingBottom: 32,
          paddingLeft: isMobile ? 16 : 32,
          paddingRight: isMobile ? 16 : 32,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div style={{ flex: 1 }}>
          <Outlet />
        </div>
      </main>
      <IOSInstallPrompt />
    </div>
  );
}
